/**
 * The transitional « pas de MCF » issuing mode (#606).
 *
 * A tenant subject to certification on a day issues ORDINARY invoices for an
 * establishment when, at that instant:
 * - no certifying device serves the establishment (a physical MCF speaking
 *   the annex protocol, or an enrolled site agent; the virtual MCF and the
 *   simulator are not one);
 * - the tenant has opted in, and has not withdrawn its opt-in;
 * - the mode is open: no platform closure counts, and the SFE is not
 *   attested on that day (no version holds an attestation dated on or
 *   before it that is neither suspended nor withdrawn on it,
 *   sfe-attestation-state.ts). On an install that passes its verified
 *   signed list, the attestations are read on the day of the later of that
 *   instant and the list's `issuedAt` (notBeforeList).
 * Otherwise a subject tenant issues CERTIFIED invoices.
 *
 * Days are calendar days in Africa/Ouagadougou (UTC, no DST), as in
 * certification-obligation.ts.
 */
import { currencyDay } from "./certification-currency.js";
import { sfeAttestedOn } from "./sfe-attestation-state.js";
import { notBeforeList } from "./sfe-version-decision.js";
import { ISSUANCE_REGIMES, hasCertificationScheme, } from "./certification-obligation.js";
/**
 * The acknowledgement an administrator accepts to opt in. `version` is
 * recorded with the opt-in and in the audit log; a new wording gets a new
 * version.
 */
export const NO_MCF_TRANSITIONAL_ACKNOWLEDGEMENT = Object.freeze({
    version: "2026-10-03",
    text: "Tant qu'aucun MCF ni agent MCF n'est disponible pour un établissement, les factures que ce logiciel émet " +
        "pour cet établissement sont des factures ordinaires : ce ne sont pas des factures certifiées. " +
        "L'entreprise reste responsable de son obligation de certification.",
});
function instant(value) {
    const time = (value instanceof Date ? value : new Date(value)).getTime();
    return Number.isNaN(time) ? Number.NaN : time;
}
function activeAt(row, at) {
    if (instant(row.createdAt) > at)
        return false;
    return row.withdrawnAt == null || instant(row.withdrawnAt) > at;
}
/** Whether the mode is open in `input.jurisdiction` at `date`. */
export function noMcfTransitionalWindow(input, date) {
    if (!hasCertificationScheme(input.jurisdiction))
        return { open: false, closedBy: "NO_SCHEME" };
    if (!input.attestationKnown)
        return { open: false, closedBy: "ATTESTATION_UNKNOWN" };
    const day = currencyDay(date);
    const attestedDay = input.list ? currencyDay(notBeforeList(new Date(instant(date)), input.list)) : day;
    if (sfeAttestedOn(input.attestations, attestedDay)) {
        return { open: false, closedBy: "SFE_ATTESTATION" };
    }
    const at = instant(date);
    const closed = input.closures.some((closure) => closure.jurisdiction === input.jurisdiction &&
        currencyDay(closure.effectiveFrom) <= day &&
        activeAt(closure, at));
    return closed ? { open: false, closedBy: "PLATFORM" } : { open: true };
}
/** Whether the tenant's opt-in is active at `date`. */
export function noMcfTransitionalOptedIn(optIns, date) {
    const at = instant(date);
    return optIns.some((optIn) => activeAt(optIn, at));
}
/** Whether the mode turns a subject tenant's issue at `date` into an ordinary one. */
export function noMcfTransitionalApplies(args) {
    return (args.subject &&
        !args.deviceAvailable &&
        noMcfTransitionalOptedIn(args.input.optIns, args.date) &&
        noMcfTransitionalWindow(args.input, args.date).open);
}
/** The regime of an issue: CERTIFIED for a subject tenant unless the mode applies. */
export function effectiveIssuanceRegime(args) {
    return args.subject && !args.transitional ? ISSUANCE_REGIMES.CERTIFIED : ISSUANCE_REGIMES.ORDINARY;
}
/**
 * Header in which an on-site node sends its clock (ISO 8601) on every pull
 * and push: the cloud measures the node's offset from it to judge the
 * `signedAt` of its ordinary invoices.
 */
export const NODE_TIME_HEADER = "x-node-time";
/**
 * Sentence appended to a refusal of ordinary issuance when the mode is open
 * and no certifying device serves the establishment, so the tenant may opt
 * in.
 */
export const NO_MCF_TRANSITIONAL_OPT_IN_HINT = "Aucun MCF ni agent MCF n'est disponible pour cet établissement : l'administrateur de l'entreprise peut " +
    "activer le mode transitoire « pas de MCF » (Paramètres › Paramétrage DGI › Obligation de certification) " +
    "pour émettre des " +
    "factures ordinaires.";
//# sourceMappingURL=no-mcf-transitional.js.map